import { Button } from "../../components/Button";
import Modal from "../../components/Modal";
import { ReactComponent as MinusIcon } from "../../assets/icons/minus.svg";
import { ReactComponent as PlusIcon } from "../../assets/icons/plus.svg";
import { useEffect, useMemo, useState } from "react";
import { useApp } from "../../services/app-context";
import { useUser } from "../../services/user-context";
import { getCallFocusTopics, getCallFocusTopicsLabelMap, getPersonalities, getPractitionerTypes, parseInfoBadgeText, resolveCustomizerHcpTag } from "../../services/utils";
import { PRAXIS_PLAY_EVENTS } from "../../integrations/posthog/events";
import { useAnalytics } from "../../integrations/posthog/useAnalytics";
import { isCallFocusEnabled } from "../../integrations/posthog/featureFlags";
import { usePostHogFeatureFlag } from "../../integrations/posthog/usePostHogFeatureFlag";
import { TooltipView } from "../../components/Tooltip";
import { twMerge } from "tailwind-merge";


function Heading({ title, subtitle, tooltip }: { title: string; subtitle?: string, tooltip?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-[2px]">
      <span className="text-text-primary text-base font-semibold">{title}</span>
      <span className="text-text-primary text-base font-bold"> </span>
      {!!subtitle && (
        <span className="text-text-primary text-base font-normal">({subtitle})</span>
      )}
      {tooltip && <TooltipView
        content={tooltip}
        id={"heading-tooltip-" + title.toLowerCase().replace(" ", "-")}
      />}
    </div>
  );
}

export function SelectButton({
  title,
  selected,
  className = "",
  onClick,
  disabled,
  tooltip,
  bgClassName = "",
}: {
  title: string;
  selected?: boolean;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
  tooltip?: React.ReactNode;
  bgClassName?: string;
}) {
  return (
    <button
      type="button"
      className={`text-[15px] flex items-center justify-center gap-2 hover:shadow font-medium py-3 px-[18px] rounded-full
        ${disabled ? "opacity-60 cursor-not-allowed" : ""}
        ${selected
          ? "border border-PraxisTerracotta bg-background2 text-PraxisTerracotta"
          : bgClassName || "text-[#434343] bg-background"
        } ${className}`}
      onClick={onClick}
      disabled={disabled}
    >
      {title}
      {tooltip && <TooltipView
        content={tooltip}
        id={"select-button-tooltip-" + title.toLowerCase().replace(" ", "-")}
        className="!z-[4]"
        containerClassName={twMerge(selected ? "[&_svg_path]:!stroke-white" : "[&_svg_path]:!stroke-black")}
      />}
    </button>
  );
}

/**
 * True when the tenant offers at least one Call Focus topic for the given
 * group (cohort if matched, otherwise `default`). When `group` is omitted
 * we only consider `default` — that's deliberate: callers should pass the
 * user's group so unrelated cohort-only topics don't enable the feature.
 */
export function hasTenantCallFocusTopics(
  tenantProfile: { topics?: unknown } | null | undefined,
  group?: string,
): boolean {
  return getCallFocusTopics(tenantProfile?.topics, group).length > 0;
}

/**
 * Comma-separated labels for selected topic values. Resolves labels from the
 * same group + practitioner-tag scope as the customizer picker; falls back to
 * the raw topic ID when the value isn't in that scoped pool.
 */
export function formatCallFocusDisplay(
  tenantProfile: { topics?: unknown } | null | undefined,
  topicValues: unknown,
  group?: string,
  practitionerTag?: string,
): string {
  if (!Array.isArray(topicValues) || topicValues.length === 0) return "";
  const labelByKey = getCallFocusTopicsLabelMap(
    tenantProfile?.topics,
    group,
    resolveCustomizerHcpTag(practitionerTag),
  );
  return topicValues
    .map((v) => {
      const val = String(v);
      return labelByKey[val] || val;
    })
    .filter(Boolean)
    .join(", ");
}

function Separator() {
  return <div className="my-[26px] border-[0.5px] border-[#DDD] mx-5" />;
}

type Form = {
  practitionerType: string;
  objectionLevel: string;
  personalityType: string;
  beginTime: number;
  isPatientBased: boolean | string;
  isCertified: boolean;
  isPracticeMode: boolean;
  topics: string[];
};

/** Payload from Continue — `beginTime` only when the time picker is shown (Praxis Play). */
export type CustomizerContinuePayload = Omit<Form, "beginTime"> & {
  beginTime?: number;
};

export function CustomizerModal({
  onClose,
  isOpen,
  onContinue,
  data,
  hcpTag,
  setHcpTag,
  groupInfo,
}: {
  onClose: () => void;
  isOpen: boolean;
  onContinue: (customizeForm: CustomizerContinuePayload) => void;
  data: Form;
  hcpTag?: string;
  setHcpTag?: (hcpTag: string) => void;
  groupInfo?: any;
}) {
  const { captureEvent } = useAnalytics();
  const { tenantProfile } = useApp();
  const info_badges = tenantProfile?.info_badges || {};
  const { userInfo } = useUser() as any;
  const group = userInfo?.group;
  const [form, setForm] = useState<Form>({
    practitionerType: "",
    objectionLevel: "M",
    personalityType: "",
    beginTime: 0,
    isPatientBased: "",
    isCertified: false,
    isPracticeMode: false,
    topics: [],
  });

  const objections: any[] = useMemo(() => {
    return tenantProfile?.objections || [];
  }, [tenantProfile]);

  const callFocusFlagOn = usePostHogFeatureFlag(isCallFocusEnabled);

  // Only scope the customizer when the group itself declares `hcpTags` (parent
  // forwards a non-empty tag). For groups without `hcpTags` the parent passes
  // an empty string and we deliberately do NOT fall back to a cohort tag —
  // doing so would hide a tagged practitioner the user just selected on the
  // HCP screen and let them submit a mismatched personality on Continue.
  // See `CLAUDE.md` (HCP Tags & Personality Scoping) and `resolveCustomizerHcpTag`.
  const effectiveHcpTag = useMemo(() => resolveCustomizerHcpTag(hcpTag), [hcpTag]);

  const callFocusTopics = useMemo(
    () => getCallFocusTopics(tenantProfile?.topics, group, effectiveHcpTag),
    [tenantProfile, group, effectiveHcpTag],
  );
  const callFocusEnabled = callFocusFlagOn && callFocusTopics.length > 0;

  // Final safety net: filter submitted topics against the *current*
  // `callFocusTopics` pool so an ID from an old cohort/hcpTag context can
  // never leak to the parent. The sanitization effect below keeps the
  // *rendered* selection in sync as the pool changes, but re-validating at
  // submit time decouples submission correctness from effect/render ordering.
  const handleContinue = (submitted: Form) => {
    let outgoingTopics: string[] = [];
    if (callFocusEnabled) {
      const validKeys = new Set(callFocusTopics.map((t) => t.key));
      outgoingTopics = submitted.topics.filter((t) => validKeys.has(t));
    }
    const payload: CustomizerContinuePayload = {
      ...submitted,
      topics: outgoingTopics,
    };
    onContinue(payload);
    captureEvent(PRAXIS_PLAY_EVENTS.HCP_SETTINGS_SAVED, {
      personalities_selected: submitted.personalityType,
      objection_level: submitted.objectionLevel,
      available_time: submitted.beginTime,
      total_customizations: 1,
    });
  };

  const handleChange = (value: string | number | boolean, key: string, image?: string) => {
    if (key === "beginTime") {
      const val = (value as number) > 15 ? 15 : (value as number) < 3 ? 3 : value;
      setForm((prev: Form) => ({ ...prev, [key]: val as number }));
      return;
    }
    if (key === "objectionLevel") {
      setForm((prev: Form) => ({ ...prev, objectionLevel: value as string }));
      return;
    }
    if (key === "practitionerType") {
      setForm((prev: Form) => {
        return {
          ...prev,
          practitionerType: value as string,
          practitionerImage: image as string
        };
      });
      return;
    }
    if (key === "topics") {
      if (!callFocusEnabled) return;
      const topicValue = value as string;
      setForm((prev: Form) => {
        const current = prev.topics ?? [];
        const has = current.includes(topicValue);
        return {
          ...prev,
          topics: has ? current.filter((t) => t !== topicValue) : [...current, topicValue],
        };
      });
      return;
    }
    setForm((prev: Form) => ({ ...prev, [key]: value }));
  };

  // Only reset local state from the parent baseline when `data` actually changes.
  // Do not tie this to callFocusEnabled or tenant reloads would clobber in-modal edits.
  useEffect(() => {
    if (!data) return;
    const topicsRaw = Array.isArray(data.topics) ? data.topics : [];
    setForm({
      ...(data as Form),
      topics: topicsRaw,
    });
  }, [data]);

  // Adjust topics only when Call Focus availability toggles — never overwrite other fields.
  // Intentionally omit `data` from deps: we only read `data.topics` when CF becomes available
  // (same render as the toggle). Listing `data`/`data.topics` would re-run on unrelated parent
  // updates and could repopulate topics after the user cleared them.
  useEffect(() => {
    if (!callFocusEnabled) {
      setForm((prev) => (prev.topics.length ? { ...prev, topics: [] } : prev));
      return;
    }
    const fromParentRaw = Array.isArray(data.topics) ? data.topics : [];
    const fromParent = fromParentRaw;
    setForm((prev) => {
      if (prev.topics.length > 0) return prev;
      if (!fromParent.length) return prev;
      return { ...prev, topics: fromParent };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see comment above
  }, [callFocusEnabled]);

  const cohortPractitionerTypes: { type: string, image: string, key: string, tag?: string }[] = useMemo(
    () => getPractitionerTypes(tenantProfile?.practitioner_types || {}, group) || [],
    [tenantProfile, group],
  );

  const practitionerTypes: { type: string, image: string, key: string, tag?: string }[] = useMemo(() => {
    if (!effectiveHcpTag) return cohortPractitionerTypes;
    return cohortPractitionerTypes.filter((type) => type.tag === effectiveHcpTag);
  }, [cohortPractitionerTypes, effectiveHcpTag]);

  const personalitiesList = useMemo(
    () => getPersonalities(tenantProfile?.personalities || {}, group, effectiveHcpTag),
    [tenantProfile, group, effectiveHcpTag],
  );

  const isPractitionerInList = practitionerTypes.some(
    (pt) => pt.key === form.practitionerType || pt.type === form.practitionerType,
  );

  const isPersonalityInList = personalitiesList.some((p) => p.key === form.personalityType);

  // Drop a stale `practitionerType` when the hcpTag filter (or cohort config) no longer
  // includes it — otherwise Continue can stay enabled via a still-valid personality while
  // the UI shows no selected practitioner pill.
  useEffect(() => {
    if (!form.practitionerType) return;
    if (isPractitionerInList) return;
    setForm((prev) => ({ ...prev, practitionerType: "" }));
  }, [practitionerTypes.length, form.practitionerType, isPractitionerInList]);

  // Drop a stale `personalityType` (carried over from parent `data` after an hcpTag
  // change, tenant profile refetch, or a cohort that ships no personalities) so the
  // rendered selection matches the value we'd actually submit. Covers both
  // "value not in the current pool" and "no pool at all" — without the second,
  // Continue would push a non-existent personality to startCall when the
  // cohort/tenant has no personalities configured. The Continue gate below also
  // disables submission for the empty-pool case, but we still clear here so the
  // internal form state stays consistent.
  useEffect(() => {
    if (!form.personalityType) return;
    if (isPersonalityInList) return;
    setForm((prev) => ({ ...prev, personalityType: "" }));
  }, [personalitiesList.length, form.personalityType, isPersonalityInList]);

  // Drop selected topics that aren't in the currently available pool so the
  // *rendered* selection stays in sync as cohort/hcpTag changes. We *filter*
  // rather than clear: this effect also fires on initial mount (and on
  // tenant-profile refetches) right after the `data` effect has copied
  // parent topics into the form — clearing here would wipe legitimate saved
  // selections every time the modal opens. Submission correctness is
  // additionally enforced in `handleContinue` so this effect can fail to run
  // (or run with stale `callFocusTopics`) without leaking invalid IDs.
  useEffect(() => {
    setForm((prev) => {
      if (!prev.topics.length) return prev;
      const validKeys = new Set(callFocusTopics.map((t) => t.key));
      const filtered = prev.topics.filter((t) => validKeys.has(t));
      if (filtered.length === prev.topics.length) return prev;
      return { ...prev, topics: filtered };
    });
  }, [callFocusTopics, isOpen]);

  useEffect(() => {
    if (isOpen) {
      captureEvent(PRAXIS_PLAY_EVENTS.HCP_CUSTOMIZATION_INITIATED, {
        source: "pre_training",
      });
    }
  }, [isOpen]);

  return (
    <Modal
      contentClassName="!px-0 !w-[800px]"
      title="Customizer"
      isOpen={isOpen}
      handleModal={onClose}
    >
      <div className="max-h-[calc(100vh-125px)] overflow-auto">
        <div className="px-5 flex flex-col md:flex-row items-center justify-between mt-2.5 gap-2">
          <div className="text-[#444] text-sm font-normal w-full md:max-w-[65%]">
            Customize the AI HCP character you would like to practice with.{" "}
          </div>
        </div>
        <Separator />

        <div className="flex flex-col md:flex-row gap-12 items-center justify-between px-5">
          {groupInfo?.hcpTags?.length > 0 && <div className="w-full flex-1 rounded-lg flex-col justify-start items-start gap-4 flex">
            <Heading title="Select a Cohort" />
            <div className="flex gap-[1px] rounded-full p-1 bg-gray-100">
              {groupInfo?.hcpTags?.map((tag: string) => (
                <button onClick={() => setHcpTag?.(tag)} key={tag} className={twMerge("text-text-gray py-2 px-8 rounded-full text-[13px] font-medium", hcpTag === tag ? "text-text-primary bg-gray2" : "")}>
                  {tag}
                </button>
              ))}
            </div>
          </div>}
          <div className="w-full flex-1 rounded-lg flex-col justify-start items-start gap-4 flex">
            <Heading title="Time AI HCP has available to begin" />

            <div className="self-stretch justify-start items-start gap-2 flex">
              <div className="w-full p-2 rounded-full border border-gray-100 justify-between items-center flex">
                <button
                  type="button"
                  onClick={() => handleChange(form.beginTime - 1, "beginTime")}
                  disabled={form.beginTime === 3}
                  className="w-11 h-11 p-3.5 bg-background2 border border-PraxisTerracotta rounded-full justify-center items-center gap-1 flex"
                >
                  <MinusIcon className="w-6 h-6 relative [&_path]:stroke-PraxisTerracotta [&_path]:stroke-2" />
                </button>
                <div className="flex items-center text-[#434343] text-base font-medium gap-1">
                  <input
                    type="text"
                    value={form.beginTime}
                    onChange={(e) => {
                      if (
                        !Number.isNaN(Number(e.target.value)) &&
                        Number(e.target.value) >= 0 &&
                        Number(e.target.value) <= 7
                      ) {
                        handleChange(Number(e.target.value), "beginTime");
                      }
                    }}
                    min={3}
                    max={15}
                    className="w-8 text-right !outline-none"
                  />
                  mins
                </div>
                <button
                  type="button"
                  disabled={form.beginTime >= 15}
                  onClick={() => handleChange(form.beginTime + 1, "beginTime")}
                  className="w-11 h-11 p-3.5 bg-background2 border border-PraxisTerracotta rounded-full justify-center items-center gap-1 flex"
                >
                  <PlusIcon className="w-6 h-6 relative [&_path]:stroke-PraxisTerracotta [&_path]:stroke-2" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <Separator />
        <div className="flex flex-col gap-4 px-5">
          <Heading
            title="AI HCP Type"
            subtitle="Select one"
          />
          <div className="items-center flex-wrap gap-2 flex flex-wrap">
            {practitionerTypes.map(({ type, key, image }: { type: string, key: string, image: string }) => (
              <SelectButton
                title={type}
                key={type}
                onClick={() => handleChange(key || type, "practitionerType", image)}
                selected={form.practitionerType === key || form.practitionerType === type}
              />
            ))}
          </div>
        </div>
        <Separator />
        <div className="flex flex-col gap-4 px-5">
          <Heading title={tenantProfile?.objections_title || "Objection"} subtitle="Select one" />
          <div className="items-center flex-wrap gap-2 flex flex-wrap">
            {objections.map(({ label, value }: { label: string, value: string }) => (
              <SelectButton
                title={label}
                key={value}
                onClick={() => handleChange(value, "objectionLevel")}
                selected={form.objectionLevel === value}
                className="min-w-[150px]"
              />
            ))}
          </div>
        </div>
        <Separator />
        {callFocusEnabled && <>
          <div className="flex flex-col gap-4 px-5">
            <Heading title="Call Focus" subtitle={`Select one or multiple`} />
            <div className="items-center flex-wrap gap-2 flex flex-wrap">
              {callFocusTopics.map(({ key, label }) => (
                <SelectButton
                  title={label}
                  key={key}
                  onClick={() => handleChange(key, "topics")}
                  selected={form.topics.includes(key)}
                  className="min-w-[150px]"
                />
              ))}
            </div>
          </div>
          <Separator />
        </>}
        <div className="flex flex-col gap-4 px-5">
          <Heading title="Personality Type" subtitle="Select one" />
          <div className="items-center flex-wrap gap-2 flex flex-wrap">
            {personalitiesList.map(({ key, label }) => (
              <SelectButton
                title={label || key}
                key={key}
                onClick={() => handleChange(key, "personalityType")}
                selected={form.personalityType === key}
              />
            ))}
          </div>
        </div>
        <Separator />

        <div className="mt-[26px] px-5">
          <Button
            variant="primary"
            className="px-7 !py-2 !text-lg w-full  !font-medium !rounded-full"
            onClick={() => handleContinue(form)}
            // The personality picker is always rendered, so we require a valid
            // selection — including the empty-pool case, where the picker shows
            // no pills and a `length > 0` guard would leave Continue enabled
            // with a stale personalityType from `data`.
            disabled={!isPractitionerInList || !isPersonalityInList}
          >
            Continue
          </Button>
        </div>
      </div>
    </Modal>
  );
}
